/**
 * FlowChatSessionsProvider — shell-level owner of Flow's backgroundable chat
 * sessions (U3).
 *
 * Today's dock has ONE chat: `chatAccountId`/`chatConversationId` on
 * `EnrollQueueContext`. This module replaces that single global pointer with
 * a keyed collection (`Map<sessionId, FlowChatSessionEntry>`) so N
 * conversations can create/stream/complete independently of whichever panel
 * happens to be visible.
 *
 * Store mechanism: a module-scope zustand store (GTMOPS-2285 Track B — the
 * house convention, see root CLAUDE.md; this file used to hand-roll
 * `useSyncExternalStore` over version-gated snapshot caches). The session
 * `entries` Map itself stays NON-reactive — it carries per-session internals
 * (`creating`, `abortController`, `generation`, `staleAskMessageId`, the live
 * `send` closure) that are read and mutated outside React, and it was never
 * the reactive surface. What IS reactive is the set of PUBLISHED fields the
 * hooks select from, one independent field per subscription channel:
 *   - `list` (`useFlowChatSessionList`) — status/ordering metadata,
 *     republished only on status transitions and message *boundaries*
 *     (never per delta).
 *   - `streams[sessionId]` (`useFlowChatSessionStream`) — the live message
 *     buffer, republished on every delta for THAT session only.
 *   - `hostedIds` — which sessions need a live `SessionStreamHost`, consumed
 *     by `FlowChatSessionsProvider` itself.
 *   - `activeSessionId` — which session the chat surface is showing.
 *
 * THE PERF INVARIANT (coding-standards.md §2, guarded by the `narrow
 * subscriptions` test): a field is republished with a NEW reference ONLY at
 * the sites that should be observable to that channel's subscribers. Every
 * mutation funnels through `publish()`, which is the ONLY writer of these
 * fields, and each action names exactly the channels it touches. In
 * particular a per-token `assistant_delta` republishes only that session's
 * `streams` entry — `list` keeps its identical array reference, so zustand's
 * `Object.is` selector check bails a list subscriber out of re-rendering.
 * Never derive `list` from `entries` inside a selector or a render.
 *
 * `SessionStreamHost` is UI-less (`return null`) and lives ABOVE the chat
 * tab's mount/unmount ternary — its lifetime is tied to the STORE, not to
 * any panel. This is what lets a stream survive a tab switch or sidebar
 * close (findings #1/#2/#7).
 *
 * No `@assistant-ui/react` import here — this file sits in the eager shell
 * chunk (coding-standards.md §6, "Eager @assistant-ui imports").
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { create } from 'zustand';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useApi, type ApiFetchInit } from '../../../lib/api';
import { useConversationStream } from '../../../view-system/hooks/useConversationStream';
import type { ErrorState } from '../../../view-system/hooks/useConversationStream';
import type { StreamMessage } from '../../../types/stream-protocol';
import type { PersonaListItem } from '../../../types/personas';
import { KEYSTONE_CONVERSATIONS_QUERY_KEY } from '../../account-view/keystone/useKeystoneConversations';
import { createKeystoneConversation } from '../../account-view/keystone/create-conversation';
import { runLazyCreate, type LazyCreatePort } from '../../account-view/keystone/useKeystoneChatSession';
import { isChatSurfaceVisible, revealChat } from '../shell-next/panels/flow-panels-store';
import {
  registerIdentityScopedReset,
  __resetIdentityScopedResetForTests,
} from '../../../lib/identity-scoped-reset';

// Public types

export type FlowChatCreateStatus = 'idle' | 'creating' | 'error';

/** Narrow list-metadata view of one session — what `useFlowChatSessionList`
 *  publishes. Deliberately excludes the message buffer (that's the stream
 *  channel, subscribed separately so a token delta never re-renders a list
 *  consumer). */
export interface FlowChatSessionSummary {
  sessionId: string;
  accountId: string;
  accountName: string;
  conversationId: string | null;
  createStatus: FlowChatCreateStatus;
  createError: string | null;
  persona: PersonaListItem | null;
  stopped: boolean;
  lastActivityAt: number;
  /**
   * The verbatim first-message text queued for a not-yet-hosted session
   * (U4 addition — session-list row preview text for a draft mid-create;
   * `null` once a host has flushed and consumed it, or when nothing has
   * been queued yet). NOT the message buffer — that's the stream channel.
   */
  pendingFirstMessage: string | null;
  /**
   * Debounced composer draft text (Wave 2 review finding #4 — "resume my
   * typing"). Written by `setDraftText` roughly every ~400ms of typing
   * (never per-keystroke) and read back once, at mount, by whichever
   * surface (`PreChatSurface`/`KeystoneChatSurface`) is showing this
   * session — so ordinary in-tab navigation (back-to-list, switching
   * sessions, switching to Queue/Requests and back) never loses an
   * unsent draft. Cleared to `''` the moment a message actually sends
   * (chat-panel-core's `handleSend`, synchronously, not debounced) so a
   * stale draft never reappears after sending. Does NOT survive a full
   * page reload — sessions themselves don't either (plan Defaults #7),
   * and no new persistence layer was introduced for this.
   */
  draftText: string;
}

/** Per-session stream snapshot published by that session's `SessionStreamHost`.
 *  `send` is present only while the session is LIVE — a frozen (stopped)
 *  snapshot omits it (the viewed-session adapter composes a resume action
 *  instead; see coding-standards.md §1's frozen-stream bullet). */
export interface FlowChatStreamSnapshot {
  messages: StreamMessage[];
  isRunning: boolean;
  error: ErrorState;
  send?: (text: string) => Promise<void>;
}

// Internal entry shape

interface SessionEntry {
  sessionId: string;
  accountId: string;
  accountName: string;
  conversationId: string | null;
  /** Synchronous in-flight guard (queue-or-drop). Read/written only inside
   *  the port passed to `runLazyCreate` — never from outside a create call. */
  creating: boolean;
  createError: string | null;
  pendingFirstMessage: string | null;
  persona: PersonaListItem | null;
  stopped: boolean;
  /** Monotonic generation token. Bumped by `stopSession` so a late-resolving
   *  create (real OR — in tests — one that resolves despite the abort) is
   *  recognized as stale via identity, never account-VALUE equality (the
   *  A→B→A bug `useKeystoneChatSession.ts:206` has). */
  generation: number;
  abortController: AbortController | null;
  lastActivityAt: number;
  stream: FlowChatStreamSnapshot | null;
  /**
   * Stop-then-ask suppression (Wave 2 review finding #3) — the `messageId`
   * of the last unanswered `'user'` ask at the moment `stopSession` froze
   * this entry, or `null` if nothing was in flight. Persists across
   * `resumeWithMessage`/`sendFirstMessage` (never cleared there) so the
   * NEXT `SessionStreamHost` mount can arm `useConversationStream`'s
   * `suppressAnswerForMessageId` and drop the eventual, wasted answer to
   * the question the rep already abandoned. See that hook's own doc
   * comment for the full mechanism and why it keys off the ASK, not the
   * answer (no wire field correlates the two).
   */
  staleAskMessageId: string | null;
  /** See `FlowChatSessionSummary.draftText`'s doc comment. */
  draftText: string;
}

interface Runtime {
  apiFetch: (input: RequestInfo | URL, init?: ApiFetchInit) => Promise<Response>;
  invalidateConversations: () => void;
}

// Store

/** The reactive surface — see this file's header comment for the per-channel
 *  breakdown and the perf invariant. Everything a consumer subscribes to
 *  lives here; everything else (the `entries` Map and its per-session
 *  internals) deliberately does not. */
interface FlowChatSessionsState {
  list: FlowChatSessionSummary[];
  hostedIds: string[];
  /** Keyed by sessionId. A `Record` (not a `Map`) keeps
   *  `useFlowChatSessionStream`'s selector a single property read, and
   *  zustand compares the SELECTED value with `Object.is` — so republishing
   *  one session's snapshot leaves every other session's selected value
   *  referentially identical and its subscribers un-rendered. */
  streams: Record<string, FlowChatStreamSnapshot | null>;
  activeSessionId: string | null;
}

/** Exported per the store convention (root CLAUDE.md) — the reset idiom is
 *  `useFlowChatSessionsStore.setState(initialState, true)`. */
export const initialState: FlowChatSessionsState = {
  list: [],
  hostedIds: [],
  streams: {},
  activeSessionId: null,
};

const useFlowChatSessionsStore = create<FlowChatSessionsState>(() => ({ ...initialState }));

/** Which published channels an action makes observable. Passing a channel
 *  means "republish it with a fresh reference"; omitting it means "this
 *  action is invisible to that channel's subscribers" — which is the perf
 *  invariant, spelled out per call site instead of hidden in version
 *  counters. `activeSessionId` is a string (referentially free), so it is
 *  passed by value and only when the action actually sets it. */
interface PublishTargets {
  list?: boolean;
  hosted?: boolean;
  streams?: readonly string[];
  activeSessionId?: string | null;
}

function createStore() {
  const entries = new Map<string, SessionEntry>();
  let runtime: Runtime | null = null;

  /** The only per-field writer of the reactive state (the two whole-state
   *  resets below — `resetAllSessions` and `__resetForTests` — replace it
   *  wholesale instead). One call per store action, so an action still
   *  produces exactly one notification, matching the single `notify()` the
   *  hand-rolled predecessor ended each action with. */
  function publish(targets: PublishTargets) {
    useFlowChatSessionsStore.setState((state) => {
      const next: Partial<FlowChatSessionsState> = {};
      if (targets.list) next.list = Array.from(entries.values()).map(toSummary);
      if (targets.hosted) {
        next.hostedIds = Array.from(entries.values())
          .filter(isHosted)
          .map((e) => e.sessionId);
      }
      if (targets.streams) {
        const streams = { ...state.streams };
        for (const id of targets.streams) streams[id] = entries.get(id)?.stream ?? null;
        next.streams = streams;
      }
      if ('activeSessionId' in targets) next.activeSessionId = targets.activeSessionId ?? null;
      return next;
    });
  }

  /** Aborts every in-flight create, drops every session entry, and resets
   *  `activeSessionId` — fired by the shared identity-scoped-reset registry
   *  (see the `registerIdentityScopedReset` call below) when the effective
   *  identity genuinely changes. Removing an entry from `entries` also drops
   *  it out of the republished `hostedIds`, so every live `SessionStreamHost`
   *  unmounts and closes its own EventSource via `useConversationStream`'s
   *  existing cleanup — no separate stream-closing code needed here.
   *
   *  Republishing `initialState`'s empty containers also drops every
   *  per-session `streams` entry: a wiped session's `useFlowChatSessionStream`
   *  must not keep serving its pre-wipe snapshot. Unobservable today only
   *  because the list republish unmounts every consumer in the same pass;
   *  cheap to close off regardless. */
  function resetAllSessions() {
    for (const e of entries.values()) {
      try {
        e.abortController?.abort();
      } catch {
        /* already settled or non-conformant controller — ignore */
      }
    }
    entries.clear();
    useFlowChatSessionsStore.setState({ ...initialState });
  }

  function toSummary(e: SessionEntry): FlowChatSessionSummary {
    return {
      sessionId: e.sessionId,
      accountId: e.accountId,
      accountName: e.accountName,
      conversationId: e.conversationId,
      createStatus: e.creating ? 'creating' : e.createError ? 'error' : 'idle',
      createError: e.createError,
      persona: e.persona,
      stopped: e.stopped,
      lastActivityAt: e.lastActivityAt,
      pendingFirstMessage: e.pendingFirstMessage,
      draftText: e.draftText,
    };
  }

  function isHosted(e: SessionEntry): boolean {
    return !e.stopped && e.conversationId !== null;
  }

  const store = {
    configureRuntime(r: Runtime) {
      runtime = r;
    },

    get(id: string): SessionEntry | undefined {
      return entries.get(id);
    },

    /** Non-reactive read, for the call sites that are not in a render (the
     *  completion-toast qualifier in `SessionStreamHost`). React consumers
     *  select `activeSessionId` through `useFlowChatSessions` instead. */
    getActiveSnapshot(): string | null {
      return useFlowChatSessionsStore.getState().activeSessionId;
    },

    /** Exposed for the module-scope `registerIdentityScopedReset` call
     *  below — never called from a component. */
    resetAllSessions,

    setActiveSessionId(id: string | null) {
      if (id) {
        const e = entries.get(id);
        if (e) e.lastActivityAt = Date.now();
      }
      publish({ list: Boolean(id), activeSessionId: id });
    },

    createSession(accountId: string, accountName: string): string {
      const sessionId = crypto.randomUUID();
      entries.set(sessionId, {
        sessionId,
        accountId,
        accountName,
        conversationId: null,
        creating: false,
        createError: null,
        pendingFirstMessage: null,
        persona: null,
        stopped: false,
        generation: 0,
        abortController: null,
        lastActivityAt: Date.now(),
        stream: null,
        staleAskMessageId: null,
        draftText: '',
      });
      publish({ list: true, activeSessionId: sessionId });
      return sessionId;
    },

    pickPersona(sessionId: string, persona: PersonaListItem) {
      const e = entries.get(sessionId);
      if (!e) return;
      e.persona = persona;
      publish({ list: true });
    },

    /** Debounced draft-text persistence (Wave 2 review finding #4) — see
     *  `FlowChatSessionSummary.draftText`'s doc comment. Callers (the
     *  composer surfaces in `chat-panel-core.tsx`) are already responsible
     *  for debouncing keystrokes before calling this; this method itself is
     *  a plain synchronous write. No-ops on an identical value so a
     *  debounced re-fire of the same text (e.g. typed then fully deleted
     *  back to what was already persisted) doesn't force a needless list
     *  re-render. */
    setDraftText(sessionId: string, text: string) {
      const e = entries.get(sessionId);
      if (!e || e.draftText === text) return;
      e.draftText = text;
      publish({ list: true });
    },

    consumePendingFirstMessage(sessionId: string): string | null {
      const e = entries.get(sessionId);
      if (!e) return null;
      const msg = e.pendingFirstMessage;
      if (msg === null) return null;
      e.pendingFirstMessage = null;
      // Wave 2 review finding — `pendingFirstMessage` is a published
      // `FlowChatSessionSummary` field (the draft-row preview text), so
      // mutating it without republishing `list` violates this file's own
      // documented invariant (a published field changed, so its channel
      // must get a fresh reference). Harmless today only by
      // coincidence — the ONE caller (`SessionStreamHost`'s flush-once
      // effect) always immediately triggers a REAL list-visible change
      // right after (a message send, which bumps activity/creating state
      // on its own) — but a future reader of this field for a session that
      // does nothing else afterward would see a stale published summary.
      publish({ list: true });
      return msg;
    },

    /** U4 addition — clears a surfaced create error (e.g. once the panel has
     *  moved it into the account-not-found full-pane state, mirroring
     *  `useKeystoneChatSession`'s own `clearCreateError`). No-ops when
     *  already clear so callers don't need to guard. */
    clearCreateError(sessionId: string) {
      const e = entries.get(sessionId);
      if (!e || e.createError === null) return;
      e.createError = null;
      publish({ list: true });
    },

    /** Publish a session's live stream (called by its `SessionStreamHost` on
     *  every render of the underlying `useConversationStream`). Republishes
     *  ONLY that session's `streams` entry — never `list` — so a
     *  high-frequency delta never re-renders a `useFlowChatSessionList`
     *  subscriber (perf invariant). */
    publishStream(sessionId: string, snapshot: FlowChatStreamSnapshot) {
      const e = entries.get(sessionId);
      if (!e || e.stopped) return;
      e.stream = snapshot;
      publish({ streams: [sessionId] });
    },

    /** A run boundary (isRunning true→false, post-replay) or an explicit
     *  send/create/view — advances `lastActivityAt` and IS visible to list
     *  subscribers (unlike a raw delta). */
    bumpActivity(sessionId: string) {
      const e = entries.get(sessionId);
      if (!e) return;
      e.lastActivityAt = Date.now();
      publish({ list: true });
    },

    /**
     * `expectedGeneration` (Wave 2 review finding, defense in depth) — the
     * generation the calling `SessionStreamHost` observed when it last
     * rendered, passed through from `useConversationStream`'s async 404
     * probe. `useConversationStream`'s own `cancelled` gate (see its own
     * doc comment) already covers the reachable case (a stop unmounts the
     * host, which sets `cancelled`), but generation is the store's OWN
     * staleness primitive everywhere else (`sendFirstMessage`'s
     * `isStale()`) — checking it here too means an unmounted/stale host can
     * never mutate a session that has since moved on (been stopped,
     * resumed, or reconnected), independent of any timing assumption about
     * exactly when `cancelled` flips. Omitting the arg (no caller does
     * today) skips the check, for callers with no generation concept.
     */
    markDeleted(sessionId: string, expectedGeneration?: number) {
      const e = entries.get(sessionId);
      if (!e) return;
      if (expectedGeneration !== undefined && e.generation !== expectedGeneration) return;
      e.stream = e.stream
        ? { ...e.stream, error: { kind: 'deleted' } }
        : { messages: [], isRunning: false, error: { kind: 'deleted' } };
      publish({ streams: [sessionId] });
    },

    sendFirstMessage(sessionId: string, text: string) {
      const entry = entries.get(sessionId);
      if (!entry) return;
      // Defense in depth (Wave 2 review, secondary fix) — mirrors
      // `useKeystoneChatSession.ts`'s `if (conversationIdRef.current) return;`.
      // Not reachable today: only `PreChatSurface` (which only renders when
      // `conversationId === null`) calls this. But without the guard, a
      // future caller invoking `sendFirstMessage` on an already-live entry
      // would POST a second conversation and rebind this session's
      // `conversationId` out from under its own host — exactly the
      // shared-slot race (findings #1/#3) this store exists to prevent.
      if (entry.conversationId) return;
      const gen = entry.generation;
      const abortController = new AbortController();
      entry.abortController = abortController;
      entry.lastActivityAt = Date.now();
      // D-H, "sending is the resume": this is ALSO the retry path for a
      // session stopped mid-create (before any conversationId ever
      // committed) — `FrozenMidCreateView`'s `onResume` routes here via
      // `session.handleSend`, not through `resumeWithMessage` (which only
      // applies to an already-committed, now-frozen conversation). Without
      // clearing `stopped` here too, a successful retry commits a
      // conversationId into an entry `isHosted` still rejects — no
      // `SessionStreamHost` ever mounts and the UI wedges on "Connecting…"
      // forever (Codex gvr.9 finding #1, BLOCK-SHIP). Idempotent no-op when
      // already false.
      entry.stopped = false;
      // Set optimistically (not only on the port's post-resolve call) so a
      // stop mid-create has something real to freeze: "the entry keeps
      // `pendingFirstMessage` visible as the frozen 'what was sent' state"
      // (U5 Approach). `runLazyCreate`'s own `setPendingFirstMessage` calls
      // below just re-assert the same trimmed value on success, or null it
      // on error — both no-ops relative to this line's value.
      const trimmedText = text.trim();
      if (trimmedText) entry.pendingFirstMessage = trimmedText;
      publish({ list: true });

      const port: LazyCreatePort = {
        tryBeginCreate: () => {
          const e = entries.get(sessionId);
          if (!e || e.creating) return false;
          e.creating = true;
          e.createError = null;
          publish({ list: true });
          return true;
        },
        endCreate: () => {
          const e = entries.get(sessionId);
          if (!e) return;
          e.creating = false;
          publish({ list: true });
        },
        setPendingFirstMessage: (msg) => {
          const e = entries.get(sessionId);
          if (!e) return;
          e.pendingFirstMessage = msg;
          publish({ list: true });
        },
        setCreateError: (msg) => {
          const e = entries.get(sessionId);
          if (!e) return;
          e.createError = msg;
          publish({ list: true });
        },
        commitConversationId: (id) => {
          const e = entries.get(sessionId);
          if (!e) return;
          e.conversationId = id;
          e.lastActivityAt = Date.now();
          publish({ list: true, hosted: true });
          runtime?.invalidateConversations();
        },
      };

      void runLazyCreate({
        text,
        buildBody: () =>
          entry.persona
            ? { selectedAccountId: entry.accountId, personaId: entry.persona.id }
            : { selectedAccountId: entry.accountId },
        createConversation: (body, signal) => {
          if (!runtime) throw new Error('FlowChatSessionsProvider is not mounted');
          return createKeystoneConversation(runtime.apiFetch, body, signal);
        },
        signal: abortController.signal,
        isStale: () => entries.get(sessionId)?.generation !== gen,
        port,
      });
    },

    focusOrCreateSessionForConversation(
      accountId: string,
      conversationId: string,
      accountName = '',
    ): string {
      for (const e of entries.values()) {
        if (e.conversationId === conversationId) {
          const wasStopped = e.stopped;
          if (wasStopped) {
            // OQ3 (resolved b): reconnect and show current server state
            // rather than the frozen client snapshot — mount a fresh host.
            e.stopped = false;
            e.stream = null;
          }
          e.lastActivityAt = Date.now();
          // U4 fix: republish this session's OWN stream entry too (not just
          // list/hosted) when un-stopping — see `stopSession`'s matching fix
          // below for why a bare mutation of `e.stream` is invisible to
          // `useFlowChatSessionStream` without it.
          publish({
            list: true,
            hosted: wasStopped,
            streams: wasStopped ? [e.sessionId] : undefined,
            activeSessionId: e.sessionId,
          });
          return e.sessionId;
        }
      }
      const sessionId = crypto.randomUUID();
      entries.set(sessionId, {
        sessionId,
        accountId,
        accountName,
        conversationId,
        creating: false,
        createError: null,
        pendingFirstMessage: null,
        persona: null,
        stopped: false,
        generation: 0,
        abortController: null,
        lastActivityAt: Date.now(),
        stream: null,
        staleAskMessageId: null,
        draftText: '',
      });
      publish({ list: true, hosted: true, activeSessionId: sessionId });
      return sessionId;
    },

    stopSession(sessionId: string) {
      const e = entries.get(sessionId);
      if (!e) return;
      // An already-settled (or otherwise hostile) controller must not be
      // able to crash the store — the generation bump below is the REAL
      // staleness guard (coding-standards.md's "no reliance on abort()
      // alone"); abort() is best-effort cleanup only (U5, Codex spec 5).
      try {
        e.abortController?.abort();
      } catch {
        /* already settled or non-conformant controller — ignore */
      }
      // Stop-then-ask suppression (Wave 2 review finding #3) — capture the
      // in-flight ask's messageId BEFORE freezing overwrites `isRunning`.
      // Only meaningful when something was actually running: an idle
      // session has no unanswered ask to protect against. Scanning
      // backward for the last `'user'` message finds the ask regardless of
      // whether its answer had already started streaming in (an
      // in-progress synthetic assistant message sits AFTER it in the
      // array, never replacing it).
      if (e.stream?.isRunning) {
        for (let i = e.stream.messages.length - 1; i >= 0; i--) {
          const m = e.stream.messages[i];
          if (m.type === 'user') {
            e.staleAskMessageId = m.messageId;
            break;
          }
        }
      }
      // Freeze BEFORE flipping `stopped` — once `stopped` is true the
      // session drops out of host membership and its `SessionStreamHost`
      // unmounts, so the frozen snapshot must already reflect the last
      // published stream state.
      const hadStream = e.stream !== null;
      if (e.stream) {
        const { send: _send, ...rest } = e.stream;
        e.stream = { ...rest, isRunning: false };
      }
      e.generation += 1;
      e.stopped = true;
      // U4 fix: the frozen `e.stream` must be republished on this session's
      // OWN stream channel — without it, `useFlowChatSessionStream`
      // subscribers kept serving the stale PRE-freeze snapshot (complete
      // with a live `isRunning:true`/`send`) after a stop, violating the
      // "a frozen stream snapshot always carries isRunning:false"
      // invariant (coding-standards.md §1).
      publish({ list: true, hosted: true, streams: hadStream ? [sessionId] : undefined });
    },

    /**
     * U4 addition — "the send is the resume" (D-H): un-stop a frozen session
     * and queue `text` as its pending first message, re-hosting it exactly
     * the way `focusOrCreateSessionForConversation`'s own un-stop branch
     * does. Reuses `SessionStreamHost`'s existing pending-first-message
     * flush-once effect rather than a second flush path — the fresh host
     * mount (triggered by the `hosted` republish) sees `pendingFirstMessage`
     * set and sends it once connected. The FULL stop/resume UI (a dedicated Stop
     * button, etc.) is bead U5's scope; this is the minimum store-level
     * primitive U4's adapter needs so a Stopped session's (already-enabled,
     * per the frozen-stream `isRunning:false` invariant) composer actually
     * works when the rep sends a message to it.
     */
    resumeWithMessage(sessionId: string, text: string) {
      const e = entries.get(sessionId);
      if (!e) return;
      e.stopped = false;
      e.stream = null;
      e.pendingFirstMessage = text;
      e.lastActivityAt = Date.now();
      // See `stopSession`'s matching fix — a bare `e.stream` mutation is
      // invisible to `useFlowChatSessionStream` without republishing this
      // session's own `streams` entry.
      publish({ list: true, hosted: true, streams: [sessionId] });
    },

    /** Test-only: drop every session and reset the published state. The store
     *  is a module-level singleton, so test files must reset it in
     *  `beforeEach` to avoid cross-test session leakage. */
    __resetForTests() {
      entries.clear();
      runtime = null;
      useFlowChatSessionsStore.setState({ ...initialState }, true);
    },
  } as const;

  return store;
}

const store = createStore();

/**
 * Identity-scoped reset (Wave 2 review finding #2), now via the shared
 * registry in `lib/identity-scoped-reset.ts` — the "did the identity really
 * change" decision (including the unresolved-identity flap guard that two
 * rounds of Spencer's QA paid for) lives there once, not copied per store.
 * The single caller that reports the current identity is
 * `useSyncEffectiveIdentity`, called by `UserControlsProvider`.
 *
 * Registered at module scope, not in an effect: the store outlives every
 * provider mount (that is what lets a live session survive a sidebar close
 * or a tab switch), so its reset has to outlive them too. The returned
 * unregister handle is deliberately dropped — the registration's lifetime is
 * the module's.
 */
function registerStoreIdentityReset() {
  registerIdentityScopedReset(() => {
    store.resetAllSessions();
  });
}
registerStoreIdentityReset();

// Hooks

export interface UseFlowChatSessionsReturn {
  activeSessionId: string | null;
  setActiveSessionId: (id: string | null) => void;
  createSession: (accountId: string, accountName: string) => string;
  sendFirstMessage: (sessionId: string, text: string) => void;
  focusOrCreateSessionForConversation: (
    accountId: string,
    conversationId: string,
    accountName?: string,
  ) => string;
  stopSession: (sessionId: string) => void;
  pickPersona: (sessionId: string, persona: PersonaListItem) => void;
  clearCreateError: (sessionId: string) => void;
  resumeWithMessage: (sessionId: string, text: string) => void;
  setDraftText: (sessionId: string, text: string) => void;
  /** Advances `lastActivityAt` and re-renders list subscribers — exposed so
   *  a send on an already-live (committed, not-first-message) session can
   *  advance ordering at the moment it's INITIATED, not only at the
   *  `isRunning true→false` run-boundary (Wave 2 review finding). */
  bumpActivity: (sessionId: string) => void;
}

// Every store method reference below is wrapped in an arrow function at its
// use site (never passed bare, e.g. `store.stopSession`). None of the store's
// methods use `this` (they close over the factory's `entries` map), but a
// bare method reference still trips the unbound-method lint rule, which
// can't see that.

export function useFlowChatSessions(): UseFlowChatSessionsReturn {
  const activeSessionId = useFlowChatSessionsStore((s) => s.activeSessionId);
  return {
    activeSessionId,
    setActiveSessionId: (id) => store.setActiveSessionId(id),
    createSession: (accountId, accountName) => store.createSession(accountId, accountName),
    sendFirstMessage: (sessionId, text) => store.sendFirstMessage(sessionId, text),
    focusOrCreateSessionForConversation: (accountId, conversationId, accountName) =>
      store.focusOrCreateSessionForConversation(accountId, conversationId, accountName),
    stopSession: (sessionId) => store.stopSession(sessionId),
    pickPersona: (sessionId, persona) => store.pickPersona(sessionId, persona),
    clearCreateError: (sessionId) => store.clearCreateError(sessionId),
    resumeWithMessage: (sessionId, text) => store.resumeWithMessage(sessionId, text),
    setDraftText: (sessionId, text) => store.setDraftText(sessionId, text),
    bumpActivity: (sessionId) => store.bumpActivity(sessionId),
  };
}

export function useFlowChatSessionList(): FlowChatSessionSummary[] {
  return useFlowChatSessionsStore((s) => s.list);
}

export function useFlowChatSessionStream(sessionId: string | null): FlowChatStreamSnapshot | null {
  // A fresh selector closure per render is fine — zustand compares the
  // selector's RETURN VALUE with `Object.is`, not the selector itself. The
  // `?? null` normalizes a session with no published snapshot yet (and a
  // wiped session, whose key is gone from `streams`) to a stable `null`, so
  // this returns a referentially identical value until THIS session's
  // snapshot is republished.
  return useFlowChatSessionsStore((s) => (sessionId ? s.streams[sessionId] ?? null : null));
}

// SessionStreamHost — UI-less, one per hosted (live, non-stopped) session

function SessionStreamHost({ sessionId }: { sessionId: string }) {
  // The conversation id a hosted session streams never changes for the
  // lifetime of the host (D-C: never rebind a live session's conversationId
  // — a conversation change means a different session entry, hence a
  // different host, keyed by sessionId in the provider below).
  const conversationId = store.get(sessionId)?.conversationId ?? null;

  // Stop-then-ask suppression (Wave 2 review finding #3) — read once at
  // whatever this host's mount happens to see; `useConversationStream`
  // itself only re-arms it on a fresh connect (its own top-of-effect
  // reset), so a value that changes on a LATER render of this same host
  // instance is a correct no-op, not a missed update — the only way this
  // ever needs to take a NEW value is a fresh host mount (resume via
  // a `hosted` republish), which reads it fresh here.
  const staleAskMessageId = store.get(sessionId)?.staleAskMessageId ?? null;

  // Defense in depth for `markDeleted` (Wave 2 review finding, belt-and-
  // suspenders alongside `useConversationStream`'s own `cancelled` gate) —
  // snapshot the generation as of this render so a 404-probe resolution
  // that fires after a stop bumped it (which also unmounts this host, so
  // this ref simply stops updating) is recognized as stale by the store's
  // own staleness primitive, not just by connection-cancellation timing.
  const deleteGuardGenerationRef = useRef(store.get(sessionId)?.generation ?? 0);
  deleteGuardGenerationRef.current = store.get(sessionId)?.generation ?? 0;

  const { messages, isRunning, error, send, replayDone, turnOutcome } = useConversationStream(conversationId, {
    onConversationDeleted: () => store.markDeleted(sessionId, deleteGuardGenerationRef.current),
    suppressAnswerForMessageId: staleAskMessageId,
  });

  const sendRef = useRef(send);
  sendRef.current = send;

  // Last-message type for the completion-toast qualifier below — read via a
  // REF (same treatment as the panel store and openChat just below), not added
  // to that effect's dep array, so a token delta never re-triggers the
  // run-boundary detector (coding-standards.md §5's per-delta prohibition).
  // `useConversationStream` only flips `isRunning` false for a protocol-
  // level `{type: 'error'}` terminal frame — it never populates the
  // separate `ErrorState`, which is what the qualifier's `error === null`
  // check actually reads (Codex gvr.9 finding #2: without this, a run that
  // ends in TIMEOUT/RESOLUTION_FAILED/QA_FAILED/INTERNAL falsely reports as
  // a successful completion).
  const lastMessageTypeRef = useRef<string | undefined>(messages[messages.length - 1]?.type);
  lastMessageTypeRef.current = messages[messages.length - 1]?.type;

  // A failed turn that streamed deltas ends with a partial `assistant` message
  // last, so the last-message type alone cannot rule it out.
  const turnOutcomeRef = useRef(turnOutcome);
  turnOutcomeRef.current = turnOutcome;

  // Backgrounding input for the completion-toast detector (U6) below. Whether
  // the Chat panel is open is read through `getState()` at transition time,
  // never as an effect dependency (coding-standards.md §5: a dep-array read
  // would re-arm the detector on every panel interaction).
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Publish every render of the underlying stream into the store. This is
  // the ONLY producer of a session's stream snapshot.
  useEffect(() => {
    store.publishStream(sessionId, { messages, isRunning, error, send });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, messages, isRunning, error, send]);

  // Run-boundary detection (isRunning true→false), gated on replay having
  // finished — `useConversationStream` toggles `isRunning` per REPLAYED
  // message on initial connect, which is not a real run boundary and must
  // not spuriously advance `lastActivityAt` / re-render list subscribers
  // (or fire a completion toast for an already-finished conversation, Codex
  // spec 4).
  //
  // The completion toast (U6) piggybacks on this SAME qualified edge — it is
  // the once-per-finish guard (do-not-create: no separate `hasToastedRef`
  // bookkeeping). `armedGenerationRef` re-captures the entry's `generation`
  // on every tick the run is observed live; comparing it at the falling edge
  // means a `stopSession` call that raced the transition (which bumps
  // `generation`) suppresses a stale completion toast (Codex spec 5) without
  // a second source of truth.
  const prevIsRunningRef = useRef<boolean | null>(null);
  const armedGenerationRef = useRef<number | null>(null);
  useEffect(() => {
    if (!replayDone) return;
    const wasRunning = prevIsRunningRef.current;
    if (isRunning) {
      armedGenerationRef.current = store.get(sessionId)?.generation ?? null;
    }
    if (wasRunning === true && isRunning === false) {
      store.bumpActivity(sessionId);

      const entry = store.get(sessionId);
      const qualifies =
        entry !== undefined &&
        !entry.stopped &&
        armedGenerationRef.current === entry.generation &&
        error === null &&
        // Only a run that ended on an answer is a success: an `error` frame, a
        // `turn_done` with the user message still last, or a `failed` turn_done
        // over a partial streamed answer is not.
        lastMessageTypeRef.current === 'assistant' &&
        turnOutcomeRef.current !== 'failed';

      if (qualifies && entry) {
        const backgrounded = !isChatSurfaceVisible() || store.getActiveSnapshot() !== sessionId;
        if (backgrounded) {
          toast.success(`${entry.accountName || 'This chat'} finished responding`, {
            action: {
              label: 'View',
              onClick: () => {
                revealChat(navigateRef.current);
                store.setActiveSessionId(sessionId);
              },
            },
          });
        }
      }
    }
    prevIsRunningRef.current = isRunning;
  }, [sessionId, isRunning, replayDone, error]);

  // Flush the queued first message exactly once. This MUST be an atomic
  // store read-and-clear (not a component-local once-guard ref) — under
  // StrictMode's setup→cleanup→setup double-invoke, the SECOND setup reads
  // back `null` because the first setup already consumed it, so no
  // duplicate POST is possible (coding-standards.md §5).
  useEffect(() => {
    const pending = store.consumePendingFirstMessage(sessionId);
    if (pending) {
      void sendRef.current(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, conversationId]);

  return null;
}

// Provider

export function FlowChatSessionsProvider({ children }: { children: ReactNode }) {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  useEffect(() => {
    store.configureRuntime({
      apiFetch,
      invalidateConversations: () =>
        queryClient.invalidateQueries({ queryKey: KEYSTONE_CONVERSATIONS_QUERY_KEY }),
    });
  }, [apiFetch, queryClient]);

  const hostedSessionIds = useFlowChatSessionsStore((s) => s.hostedIds);

  return (
    <>
      {hostedSessionIds.map((id) => (
        <SessionStreamHost key={id} sessionId={id} />
      ))}
      {children}
    </>
  );
}

/** Test-only escape hatch for resetting module-level store state between test
 *  cases (the store is a singleton). Not exported from the barrel.
 *
 *  Also resets the shared identity registry, matching what the store's own
 *  `lastEffectiveIdentityKey = null` did before U4 moved that bookkeeping
 *  out: without it, the previous test's recorded key makes the next test's
 *  first reported identity look like a genuine change and wipes the sessions
 *  that test just created. Clearing the registry drops every registration,
 *  including this store's module-scope one, so re-register right after. */
export function __resetFlowChatSessionsStoreForTests() {
  store.__resetForTests();
  __resetIdentityScopedResetForTests();
  registerStoreIdentityReset();
}
